import React, { useState } from 'react';
import styled from 'styled-components';
import { COLOR, FONT_SIZE, RADIUS, SPACING } from '../../../constants/theme';
import useMediaQuery from '../../../hooks/useMediaQuery';
import {
  getSlotPeriod,
  groupTimeslotsByPeriod,
} from '../../../utils/timeslotUtils';

// pbt-ui switches to its phone layout, with the part-of-day tabs, at 900px.
const PERIOD_TABS_MEDIA_QUERY = '(max-width: 900px)';

const PeriodTabs = styled.div`
  display: flex;
  gap: ${SPACING.xs};
  padding: ${SPACING.xs};
  margin-bottom: ${SPACING.md};
  border-radius: ${RADIUS.card};
  background: ${COLOR.mutedBackground};
`;

const PeriodTab = styled.button`
  flex: 1;
  min-height: 40px;
  border: 0;
  border-radius: ${RADIUS.md};
  background: transparent;
  color: ${COLOR.textSecondary};
  font-size: ${FONT_SIZE.body};
  font-weight: 600;
  cursor: pointer;

  &[aria-pressed='true'] {
    background: ${COLOR.surface};
    color: ${COLOR.primary};
    box-shadow: 0 1px 2px rgba(23, 29, 74, 0.12);
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(84px, 1fr));
  gap: ${SPACING.sm};

  &.three-columns {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

const Slot = styled.button`
  height: 42px;
  border: 1px solid ${COLOR.primaryBorder};
  border-radius: ${RADIUS.md};
  background: ${COLOR.primaryLight};
  color: ${COLOR.primary};
  font-size: ${FONT_SIZE.value};
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  cursor: pointer;

  &:hover {
    background: ${COLOR.primaryBorder};
  }

  &[aria-pressed='true'] {
    border-color: ${COLOR.primary};
    background: ${COLOR.primary};
    color: ${COLOR.surface};
  }
`;

const EmptyMessage = styled.p`
  margin: 0;
  font-size: ${FONT_SIZE.body};
  color: ${COLOR.textSecondary};
`;

const SlotList = ({ timeslots, selectedTimeslot, onSelect, ...props }) => (
  <Grid {...props}>
    {timeslots.map(({ value, label }) => (
      <Slot
        key={value}
        type="button"
        aria-pressed={value === selectedTimeslot}
        onClick={() => onSelect(value)}
      >
        {label}
      </Slot>
    ))}
  </Grid>
);

// Up to 900px wide a day's times are split into Morning / Afternoon / Evening tabs
// over a three-column grid, as in pbt-ui. Opens on the selected time's part of the
// day, otherwise the first with times. Wider screens show every time in one grid.
const TimeslotGrid = ({
  timeslots = [],
  selectedTimeslot,
  onSelect,
  emptyMessage,
}) => {
  const showPeriodTabs = useMediaQuery(PERIOD_TABS_MEDIA_QUERY);
  const [pickedPeriod, setPickedPeriod] = useState(null);

  if (!timeslots.length) return <EmptyMessage>{emptyMessage}</EmptyMessage>;

  if (!showPeriodTabs) {
    return (
      <SlotList
        timeslots={timeslots}
        selectedTimeslot={selectedTimeslot}
        onSelect={onSelect}
      />
    );
  }

  const groups = groupTimeslotsByPeriod(timeslots);
  const preferredPeriod =
    pickedPeriod ||
    (selectedTimeslot ? getSlotPeriod({ value: selectedTimeslot }) : null);
  const currentGroup =
    groups.find(({ key }) => key === preferredPeriod) || groups[0];

  return (
    <>
      {groups.length > 1 && (
        <PeriodTabs role="group" aria-label="Part of the day">
          {groups.map(({ key, label }) => (
            <PeriodTab
              key={key}
              type="button"
              aria-pressed={key === currentGroup.key}
              onClick={() => setPickedPeriod(key)}
            >
              {label}
            </PeriodTab>
          ))}
        </PeriodTabs>
      )}
      <SlotList
        role="group"
        aria-label={`${currentGroup.label} times`}
        className="three-columns"
        timeslots={currentGroup.timeslots}
        selectedTimeslot={selectedTimeslot}
        onSelect={onSelect}
      />
    </>
  );
};

export default TimeslotGrid;
